import React, { useEffect, useMemo, useRef, useState } from "react";
import Viewer from "react-viewer";
import { cacheService, mediaService } from "../../../Core/Api/services";
import { DbAssetObjectInfos } from "../../../Core/Api/Types/db-asset-object-infos";
import { Alert, Box, Chip, Paper, Stack, Tab, Tabs, Typography } from "@mui/material";
import PosterImage from "../../Misc/PosterImage";
import { FileCategory } from "../../../Core/Api/Types/file-category";
import DocViewer, { DocViewerRenderers } from "@cyntler/react-doc-viewer";
import TableSkeleton from "../../Table/TableSkeleton";
import { FileStatus } from "../../../Core/Api/Types/file-status";
import ReactPlayerWrapper from "../../MediaPlayer/ReactPlayerWrapper/player-wrapper";
import { GetPlayerInput, SetPlayerInput } from "../../../Redux/Reducers/redux-slice-react-player";
import { useDispatch } from "react-redux";
import { PreviewContext } from "../../../Pages/admin/Inventory/InventoryAssetSingle/AssetSinglePreview/asset-single-preview";
import { TTMLParseResult } from "../../../Core/Api/Types/ttml-parse-result";
import { GridColDef, useGridApiRef } from "@mui/x-data-grid-premium";
import { useAppDispatch, useAppSelector } from "../../../Redux/redux-store";
import { GetMetadataByTagName } from "../../../Core/Utils/asset-object-utils";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import DataTable, { CellSize } from "../../Table/table";
import { ObjectType } from "../../../Core/Api/Types/object-type";
import { GetPlayerInfos, SetPlayerHeight, SetPlayerResponsive } from "../../../Redux/Reducers/redux-slice-layout";
import { ObjectLayer } from "../../../Core/Api/Types/object-layer";
import { useTranslation } from "react-i18next";
import { GetTreeObj, SetTreeObj, TreeContext } from "../../../Redux/Reducers/redux-slice-tree";
import AssetTreeView from "../../TreeView/TitleTreeView/asset-tree-view-2";

interface PlayerProps {
  asset: DbAssetObjectInfos;
  context?: PreviewContext;
}
interface VideoPlayerProps extends PlayerProps {}
interface AudioPlayerProps extends PlayerProps {}
interface ImagePlayerProps extends PlayerProps {}
interface FilePlayerProps extends PlayerProps {}
interface SubtitlePlayerProps extends PlayerProps {}

export const AudioPlayer = (props: AudioPlayerProps) => {
  // Props & Hooks
  const { asset, context = PreviewContext.DistribEnlarged } = props;

  // Store
  const dispatch = useDispatch();
  dispatch(SetPlayerInput({ objectId: asset.id, episode: null, shareMediaKey: null }));
  return <ReactPlayerWrapper assetType={ObjectType.Audio} context={context}></ReactPlayerWrapper>;
};

export const VideoPlayer = (props: VideoPlayerProps) => {
  // Props & Hooks
  const { asset, context = PreviewContext.DistribEnlarged } = props;
  const dispatch = useDispatch();
  const playerInput = useAppSelector(GetPlayerInput);
  // Store
  useEffect(() => {
    dispatch(SetPlayerInput({ objectId: asset.id, episode: null, shareMediaKey: null }));
  }, [asset?.id]);

  if (asset.id !== playerInput.objectId) return <></>;
  return <ReactPlayerWrapper assetType={ObjectType.Video} context={context}></ReactPlayerWrapper>;
};

export const ImagePlayer = (props: ImagePlayerProps) => {
  // Props & Hooks
  const { asset } = props;

  // States
  const [visible, setVisible] = React.useState(false);

  // Cache
  let urlImage = `${mediaService.GetServiceUrl()}/image-previewer/${asset?.id}/${cacheService.GetAuthKey()}`;

  return (
    <>
      <PosterImage
        img={{ src: urlImage, loading: "eager" }}
        showLoader={true}
        onClick={() => {
          setVisible(true);
        }}
        sx={{ cursor: "pointer", img: { objectFit: "contain !important" }, height: "100%" }}
      />
      <Viewer
        visible={visible}
        noNavbar={true}
        zIndex={2001}
        onClose={() => {
          setVisible(false);
        }}
        images={[{ src: urlImage, alt: "Image viewer" }]}
      />
    </>
  );
};

export const VideoAndTarPlayer = (props: VideoPlayerProps) => {
  const { asset, context = PreviewContext.DistribEnlargedTabs } = props;
  const { t } = useTranslation();
  const tabsContainer = useRef<HTMLDivElement>(null);
  const { containerHeight } = useAppSelector(GetPlayerInfos);
  const dispatch = useAppDispatch();

  const [layout, setLayout] = useState<"player" | "tree">("player");

  const onLayoutChange = (e: React.SyntheticEvent, value: "player" | "tree") => {
    setLayout(value);
  };

  useEffect(() => {
    if (tabsContainer.current) {
      dispatch(SetPlayerHeight(containerHeight - tabsContainer.current.clientHeight));
      dispatch(SetPlayerResponsive(false));
    }

    const resizeObserver = new ResizeObserver((event) => {
      dispatch(SetPlayerHeight(containerHeight - event[0].contentBoxSize[0].blockSize));
      dispatch(SetPlayerResponsive(false));
    });

    if (tabsContainer.current) resizeObserver.observe(tabsContainer.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [tabsContainer.current, containerHeight]);

  return (
    <>
      <Tabs value={layout} onChange={onLayoutChange} sx={{ width: "100%", px: 1 }} ref={tabsContainer}>
        <Tab label={t("common:player")} value="player" />
        <Tab label={t("page:tab.label.tree")} value="tree" />
      </Tabs>
      {layout === "player" && <VideoPlayer asset={asset} context={context} />}
      {layout === "tree" && <TarPlayer asset={asset} context={context} />}
    </>
  );
};

export const TarPlayer = (props: VideoPlayerProps) => {
  const { t } = useTranslation();
  const { asset, context = PreviewContext.DistribEnlarged } = props;

  const dispatch = useAppDispatch();

  const treeObj = useAppSelector(GetTreeObj(TreeContext.AssetTarPreview));
  const [isPreviewDispo, setIsPreviewDispo] = useState<boolean>(false);

  const MakeJsTreeData = (nodeSrc, nodeDst: DbAssetObjectInfos): DbAssetObjectInfos => {
    for (let f = 0; f < nodeSrc.files.length; ++f) {
      nodeDst.childObjects!.push(
        new DbAssetObjectInfos({
          $type: "VDM.AirLab.Sdk.Core.Database.Types.DbAssetObjectInfos, VDM.AirLab.Sdk.Core",
          id: Math.random(),
          name: nodeSrc.files[f].name,
          type: ObjectType.Generic,
        })
      );
    }

    for (let d = 0; d < nodeSrc.folders.length; ++d) {
      nodeDst.childObjects!.push(
        new DbAssetObjectInfos({
          $type: "VDM.AirLab.Sdk.Core.Database.Types.DbAssetObjectInfos, VDM.AirLab.Sdk.Core",
          id: Math.random(),
          name: nodeSrc.folders[d].name,
          childObjects: [],
          type: ObjectType.Folder,
        })
      );

      MakeJsTreeData(nodeSrc.folders[d], nodeDst.childObjects![nodeDst.childObjects!.length - 1]);

      if (
        nodeDst.childObjects![nodeDst.childObjects!.length - 1].childObjects === undefined ||
        nodeDst.childObjects![nodeDst.childObjects!.length - 1].childObjects === null ||
        nodeDst!.childObjects![nodeDst.childObjects!.length - 1].childObjects!.length <= 0
      )
        nodeDst.childObjects!.pop();
    }

    return nodeDst;
  };

  useEffect(() => {
    if (asset.id !== undefined)
      mediaService.GetFilesByObjectId(asset.id).then(async (response) => {
        console.log(response);
        let treeGraphFile = response.files!.find((x) => x.formatInfos && x.formatInfos.name === "Tree Graph Json");
        let apiDownloadMediaFileUrl = mediaService.GetServiceUrl();

        if (treeGraphFile !== undefined) {
          const downloadTreegraphPath: string | null =
            apiDownloadMediaFileUrl +
            "/media-infos/object/" +
            asset.id +
            "/" +
            treeGraphFile.id +
            "/" +
            cacheService.GetAuthKey();
          try {
            const resp = await fetch(downloadTreegraphPath!);
            if (!resp.ok) {
              throw new Error("Network response was not ok");
            }
            const jsonData = await resp.json();
            setIsPreviewDispo(true);

            const result = MakeJsTreeData(
              jsonData[0],
              new DbAssetObjectInfos({
                $type: "VDM.AirLab.Sdk.Core.Database.Types.DbAssetObjectInfos, VDM.AirLab.Sdk.Core",
                childObjects: [],
                id: 1,
                name:
                  response.files!.find((x) => x.properties.fileInfos.extension !== ".json")?.name ??
                  asset.id.toString(),
                type: ObjectType.Archive,
                layer: ObjectLayer.Head,
              })
            );

            dispatch(SetTreeObj({ context: TreeContext.AssetTarPreview, obj: result }));
          } catch (error) {
            console.log(error);
            setIsPreviewDispo(false);
          }
        }
      });
  }, [asset.id]);

  return (
    <>
      {isPreviewDispo ? (
        <Box sx={{ height: "100%", overflowY: "scroll", p: 2 }}>
          {treeObj ? (
            <AssetTreeView rootObjectId={0} context={TreeContext.AssetTarPreview} disableNavigation disableSelection />
          ) : (
            <TableSkeleton />
          )}
        </Box>
      ) : (
        <Stack p={2}>
          <Typography>{t("page:body.noPreviewAvailable")}</Typography>
        </Stack>
      )}
    </>
  );
};

// @TODO Implement transcript player
export const SubtitlePlayer = (props: SubtitlePlayerProps) => {
  const { t } = useTranslation();
  const [ttml, setTtmlData] = React.useState<TTMLParseResult>();
  const { context = PreviewContext.DistribEnlarged, asset } = props;
  const [isLoading, setIsLoading] = React.useState(true);
  const apiGrid = useGridApiRef();
  const { containerHeight } = useAppSelector(GetPlayerInfos);

  const columns: GridColDef[] = [
    {
      field: "number",
      headerName: t("table:headerName.number"),
      flex: CellSize.xxsmall,
      minWidth: CellSize.xxsmall,
    },
    {
      field: "timecodeIn",
      headerName: t("common:timecodeIn"),
      flex: CellSize.small,
      minWidth: CellSize.small,
      renderCell: (data: any) => <Chip label={data.value} color="default" />,
    },
    {
      field: "timecodeOut",
      headerName: t("common:timecodeOut"),
      flex: CellSize.small,
      minWidth: CellSize.small,
      renderCell: (data: any) => <Chip label={data.value} color="default" />,
    },
    {
      field: "styleId",
      headerName: t("table:headerName.style"),
      flex: CellSize.small,
      minWidth: CellSize.small,
      renderCell: (data: any) => <Chip label={data.value} color="default" />,
    },
    {
      field: "regionId",
      headerName: t("table:headerName.region"),
      flex: CellSize.small,
      minWidth: CellSize.small,
      renderCell: (data: any) => <Chip label={data.value} color="default" />,
    },
    {
      field: "text",
      headerName: t("common:text"),
      flex: CellSize.xlarge,
      minWidth: CellSize.xlarge,
    },
  ];

  // Exports
  const subtitleExportName = useMemo(() => {
    if (!asset) return "";

    const episodenumber = GetMetadataByTagName(asset!, "serieEpisodeNumberProduction")?.value;

    return `${asset?.id}_${asset?.parentTitleName}_${episodenumber ? `${episodenumber}_` : ""}${
      GetMetadataByTagName(asset!, "languageSubtitle")?.value.value
    }_${GetMetadataByTagName(asset!, "subtitleType")?.value}`;
  }, [asset]);

  const exportCsv = () => {
    apiGrid.current.exportDataAsCsv({
      fileName: subtitleExportName,
      delimiter: GlobalConst.EXPORT_DELIMITER,
      allColumns: true,
    });
  };
  const exportExcel = () => {
    apiGrid.current.exportDataAsExcel({ fileName: subtitleExportName, allColumns: true });
  };

  useEffect(() => {
    if (asset.id !== undefined)
      mediaService.GetSubtitleParsedInfosByObjectId(asset.id).then((response) => {
        if (response.ttmlParsedInfos !== null) setTtmlData(response.ttmlParsedInfos);
        setIsLoading(false);
      });
  }, [asset.id]);
  return (
    <Stack
      px={2}
      height={
        [PreviewContext.DriveEnlarged, PreviewContext.DistribEnlarged].includes(context)
          ? containerHeight ?? "0"
          : "100%"
      }
      sx={(theme) => ({
        background: theme.palette.background.paper,
        borderBottom: `1px solid ${theme.palette.divider}`,
      })}
    >
      {!isLoading && ttml?.subtitles && context !== PreviewContext.DriveFolderPreview && (
        <DataTable
          rows={ttml?.subtitles!}
          columns={columns}
          checkboxSelection={false}
          disableRowSelectionOnClick
          pagination={true}
          getRowId={(row) => row.number}
          getRowHeight={() => "auto"}
          apiRef={apiGrid}
          initialState={{
            columns: {
              columnVisibilityModel: {
                regionId: false,
                styleId: false,
              },
            },
            pinnedColumns: { left: ["__check__"], right: ["actions"] },
          }}
        />
      )}
      {!isLoading && !ttml?.subtitles && context === PreviewContext.DriveFolderPreview && (
        <Typography textAlign={"center"} variant="body1" color="text.secondary">
          {t("page:body.previewNotAvailabelForSubtitles")}
        </Typography>
      )}
      {!isLoading && !ttml?.subtitles && context !== PreviewContext.DriveFolderPreview && (
        <Alert severity="warning">{t("alert:warning.noSubtitlesFound")}</Alert>
      )}
      {isLoading && <TableSkeleton />}
    </Stack>
  );
};

export const FilePlayer = (props: FilePlayerProps) => {
  const [fileUrl, setFileUrl] = React.useState("");
  const [fileName, setFileName] = React.useState("");
  const [fileExtension, setFileExtension] = React.useState("");

  const { asset } = props;

  useEffect(() => {
    if (asset.id !== undefined)
      mediaService.GetFilesByObjectId(asset.id).then((response) => {
        if (response.success !== null) {
          // Match online native file
          let match = response.files?.find((x) => x.category === FileCategory.Native && x.status === FileStatus.Online);

          if (match) {
            let url = `${mediaService.GetServiceUrl()}/media-infos/object/${asset.id}/${
              match.id
            }/${cacheService.GetAuthKey()}/${match.name?.replaceAll("/", "") ?? "preview.pdf"}`;

            setFileUrl(url);

            if (match.name) setFileName(match.name);
            if (match.extension) setFileExtension(match.extension);
          }
        }
      });
  }, [asset.id]);

  return <DocPreviewer fileUrl={fileUrl} fileExtension={fileExtension} />;
};

export const DocPreviewer = (props: { fileUrl: string; fileExtension: string }) => {
  const { t } = useTranslation();
  const { fileUrl, fileExtension } = props;
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [fileLoaded, setFileLoaded] = useState<boolean>(false);

  const handleDownload = async () => {
    try {
      const response = await fetch(fileUrl, { method: "GET" });
      if (!response.ok) {
        setErrorMessage(t("alert:error.downloadNotAllowed"));
        setFileLoaded(false);
      } else {
        setFileLoaded(true);
        setErrorMessage("");
      }
    } catch (error) {
      setErrorMessage(t("alert:error.downloadException"));
      setFileLoaded(false);
    }
  };

  React.useEffect(() => {
    if (fileUrl !== "") handleDownload();
  }, [fileUrl]);

  return (
    <Paper
      sx={{
        backgroundImage: "none",
        minHeight: 500,
        maxHeight: 1000,
        height: "100%",
        overflow: "hidden",
      }}
    >
      {fileUrl !== "" && !fileLoaded && <Alert severity="info">{errorMessage}</Alert>}
      {fileUrl !== "" && fileLoaded && fileExtension.toLowerCase() !== ".pdf" && (
        <DocViewer
          pluginRenderers={DocViewerRenderers}
          documents={[{ uri: fileUrl }]}
          config={{
            header: {
              disableHeader: true,
              disableFileName: true,
              retainURLParams: false,
            },
          }}
          style={{ minHeight: 500, maxHeight: 1000 }}
        />
      )}
      {fileUrl !== "" && fileLoaded && fileExtension.toLowerCase() === ".pdf" && (
        <object data={fileUrl} type="application/pdf" width="100%" height="100%">
          <iframe src={fileUrl} width="100%" height="100%">
            <p>
              {t("component:body.browserNotSupportPdfs")}
              <a href={fileUrl}>{t("button:downloadThePdf")}</a>.
            </p>
          </iframe>
        </object>
      )}
      {fileUrl === "" && <TableSkeleton />}
    </Paper>
  );
};
